import React, { useEffect, useState, memo, type SyntheticEvent, useRef } from 'react';
import { CircularProgress, Typography, Tab } from '@mui/material';
import type { CellValue, CellFormulaValue, CellRichTextValue, CellHyperlinkValue, CellErrorValue } from 'exceljs';
import ExcelJS from 'exceljs';
import { TableChart } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import {
  Container,
  ErrorBox,
  Footer,
  LoadingBox,
  OverlayImage,
  SheetContent,
  SheetScrollArea,
  StyledCol,
  StyledTable,
  StyledTabs,
  StyledTd,
  StyledTr,
} from './XlsxPreview.styles';

const COL_WIDTH_FACTOR = 7.5;
const ROW_HEIGHT_FACTOR = 1.33;

interface SheetImage {
  url: string;
  x: number;
  y: number;
  w: number;
  h: number;
}

interface CellData {
  value: string | number;
  align: React.CSSProperties['textAlign'];
  vAlign: React.CSSProperties['verticalAlign'];
  bold: boolean;
  color: string;
  bgColor: string | null;
  borderTop: boolean;
  rowSpan: number;
  colSpan: number;
  hidden: boolean;
}

interface SheetRow {
  index: number;
  height: number;
  cells: CellData[];
}

interface ParsedSheet {
  id: number;
  name: string;
  rows: SheetRow[];
  images: SheetImage[];
  colWidths: number[];
  totalWidth: number;
}

interface Props {
  fileBlob: Blob | null;
}

interface ExcelImageRange {
  tl: { nativeCol: number; nativeColOff: number; nativeRow: number; nativeRowOff: number };
  ext: { width: number; height: number };
}

const getSafeCellValue = (cellValue: CellValue | undefined | null): string | number => {
  if (cellValue === null || cellValue === undefined) return '';

  if (typeof cellValue === 'object') {
    if (cellValue instanceof Date) {
      return cellValue.toLocaleDateString();
    }

    if ('formula' in cellValue) {
      const formulaCell = cellValue as CellFormulaValue;
      if (formulaCell.result !== undefined) {
        if (typeof formulaCell.result === 'object' && formulaCell.result !== null) {
          return '#ERR';
        }
        return formulaCell.result as string | number;
      }
      return '';
    }

    if ('richText' in cellValue) {
      const richTextCell = cellValue as CellRichTextValue;
      return richTextCell.richText.map((rt) => rt.text).join('');
    }

    if ('text' in cellValue) {
      return (cellValue as CellHyperlinkValue).text;
    }

    if ('error' in cellValue) {
      return (cellValue as CellErrorValue).error || '#ERR';
    }
    return '';
  }

  if (typeof cellValue === 'boolean') {
    return cellValue ? 'TRUE' : 'FALSE';
  }

  return cellValue;
};

const XlsxPreviewBase = ({ fileBlob }: Props) => {
  const { t } = useTranslation();
  const [workbookData, setWorkbookData] = useState<ParsedSheet[]>([]);
  const [activeSheetIndex, setActiveSheetIndex] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const objectUrls = useRef<string[]>([]);

  useEffect(() => {
    return () => {
      objectUrls.current.forEach((url) => URL.revokeObjectURL(url));
      objectUrls.current = [];
    };
  }, []);

  useEffect(() => {
    if (!fileBlob) return;

    const parse = async () => {
      setLoading(true);
      setError(null);

      try {
        const buffer = await fileBlob.arrayBuffer();
        const workbook = new ExcelJS.Workbook();
        await workbook.xlsx.load(buffer);

        const parsedSheets: ParsedSheet[] = [];

        workbook.eachSheet((worksheet, sheetId) => {
          const colWidthsPx: number[] = [];
          const colXPositions: number[] = [0];
          let totalWidth = 0;

          const maxCol = Math.max(worksheet.columnCount, 10);

          for (let i = 1; i <= maxCol; i++) {
            const col = worksheet.getColumn(i);
            const w = (col.width || 8.43) * COL_WIDTH_FACTOR;
            colWidthsPx.push(w);
            totalWidth += w;
            colXPositions[i] = totalWidth;
          }

          const mergeMap = new Map<string, { r: number; c: number }>();
          const hiddenCells = new Set<string>();

          if (worksheet.model.merges) {
            worksheet.model.merges.forEach((rangeStr: string) => {
              const match = rangeStr.match(/([A-Z]+)([0-9]+):([A-Z]+)([0-9]+)/);
              if (match) {
                const startColStr = match[1];
                const startRow = parseInt(match[2], 10);
                const endColStr = match[3];
                const endRow = parseInt(match[4], 10);

                const colToInt = (col: string) => {
                  let val = 0;
                  for (let i = 0; i < col.length; i++) {
                    val = val * 26 + (col.charCodeAt(i) - 64);
                  }
                  return val;
                };

                const startCol = colToInt(startColStr);
                const endCol = colToInt(endColStr);

                const rowspan = endRow - startRow + 1;
                const colspan = endCol - startCol + 1;

                mergeMap.set(`${startRow}:${startCol}`, { r: rowspan, c: colspan });

                for (let r = startRow; r <= endRow; r++) {
                  for (let c = startCol; c <= endCol; c++) {
                    if (r === startRow && c === startCol) continue;
                    hiddenCells.add(`${r}:${c}`);
                  }
                }
              }
            });
          }

          const rows: SheetRow[] = [];
          const rowYPositions: number[] = [0];
          let currentY = 0;

          worksheet.eachRow({ includeEmpty: true }, (row, rowNumber) => {
            const prevRowIdx = rows.length > 0 ? rows[rows.length - 1].index : 0;
            const gap = rowNumber - prevRowIdx - 1;

            if (gap > 0) currentY += gap * (15 * ROW_HEIGHT_FACTOR);

            rowYPositions[rowNumber] = currentY;
            const h = (row.height || 15) * ROW_HEIGHT_FACTOR;

            const cells: CellData[] = [];
            for (let i = 1; i <= maxCol; i++) {
              const cell = row.getCell(i);

              const cellKey = `${rowNumber}:${i}`;
              const isHidden = hiddenCells.has(cellKey);
              const mergeInfo = mergeMap.get(cellKey);

              const safeValue = getSafeCellValue(cell.value);

              let bgColor: string | null = null;
              const fill = cell.fill as ExcelJS.FillPattern;
              if (fill?.type === 'pattern' && fill.fgColor?.argb) {
                const argb = fill.fgColor.argb;
                if (typeof argb === 'string') {
                  bgColor = argb.length === 8 ? `#${argb.substring(2)}` : `#${argb}`;
                }
              }

              let align: React.CSSProperties['textAlign'] = (cell.alignment?.horizontal as React.CSSProperties['textAlign']) || 'left';
              if (!cell.alignment?.horizontal && typeof safeValue === 'number') align = 'right';

              cells.push({
                value: safeValue,
                align: align,
                vAlign: (cell.alignment?.vertical as React.CSSProperties['verticalAlign']) || 'bottom',
                bold: cell.font?.bold || false,
                color: cell.font?.color?.argb && typeof cell.font?.color?.argb === 'string' ? `#${cell.font.color.argb.substring(2)}` : '#000',
                bgColor: bgColor,
                borderTop: !!cell.border,
                rowSpan: mergeInfo ? mergeInfo.r : 1,
                colSpan: mergeInfo ? mergeInfo.c : 1,
                hidden: isHidden,
              });
            }

            rows.push({ index: rowNumber, height: h, cells });
            currentY += h;
          });

          const images: SheetImage[] = [];
          const media = worksheet.getImages();

          media.forEach((m) => {
            const imageId = Number(m.imageId);
            const img = workbook.getImage(imageId);

            if (!img || !img.buffer) return;

            const blob = new Blob([img.buffer], { type: `image/${img.extension}` });
            const url = URL.createObjectURL(blob);
            objectUrls.current.push(url);

            const imageRange = m.range as unknown as ExcelImageRange;
            const tl = imageRange.tl;
            const ext = imageRange.ext;

            let x = colXPositions[tl.nativeCol] || 0;
            x += (tl.nativeColOff || 0) / 9525;

            let y: number;
            const rowIndex = tl.nativeRow + 1;

            if (rowYPositions[rowIndex] !== undefined) {
              y = rowYPositions[rowIndex];
            } else {
              y = tl.nativeRow * (15 * ROW_HEIGHT_FACTOR);
            }
            y += (tl.nativeRowOff || 0) / 9525;

            images.push({ url, x, y, w: ext.width, h: ext.height });
          });

          parsedSheets.push({
            id: sheetId,
            name: worksheet.name,
            rows,
            images,
            colWidths: colWidthsPx,
            totalWidth,
          });
        });

        if (parsedSheets.length === 0) setError(t('user.detail.file.preview.noData'));
        setWorkbookData(parsedSheets);
        setActiveSheetIndex(0);
      } catch (e) {
        setError(t('user.detail.file.preview.readError'));
      } finally {
        setLoading(false);
      }
    };

    parse();
  }, [fileBlob, t]);

  const handleChange = (_event: SyntheticEvent, newValue: number) => {
    setActiveSheetIndex(newValue);
  };

  if (loading) {
    return (
      <LoadingBox>
        <CircularProgress />
      </LoadingBox>
    );
  }

  if (error) {
    return (
      <ErrorBox>
        <Typography color="error">{error}</Typography>
      </ErrorBox>
    );
  }

  const activeSheet = workbookData[activeSheetIndex];
  if (!activeSheet) return null;

  return (
    <Container>
      <SheetScrollArea>
        <SheetContent>
          {activeSheet.images.map((img, idx) => (
            <OverlayImage key={idx} src={img.url} $x={img.x} $y={img.y} $w={img.w} $h={img.h} />
          ))}

          <StyledTable $totalWidth={activeSheet.totalWidth}>
            <colgroup>
              {activeSheet.colWidths.map((w, i) => (
                <StyledCol key={i} $width={w} />
              ))}
            </colgroup>
            <tbody>
              {activeSheet.rows.map((row) => (
                <StyledTr key={row.index} $height={row.height}>
                  {row.cells.map((cell, cIdx) => {
                    if (cell.hidden) return null;

                    return (
                      <StyledTd
                        key={cIdx}
                        colSpan={cell.colSpan}
                        rowSpan={cell.rowSpan}
                        $bgColor={cell.bgColor || 'transparent'}
                        $textAlign={cell.align}
                        $verticalAlign={cell.vAlign}
                        $isBold={cell.bold}
                        $fontColor={cell.color}
                        $hasBorderTop={cell.borderTop}>
                        {cell.value}
                      </StyledTd>
                    );
                  })}
                </StyledTr>
              ))}
            </tbody>
          </StyledTable>
        </SheetContent>
      </SheetScrollArea>

      <Footer>
        <StyledTabs value={activeSheetIndex} onChange={handleChange} variant="scrollable" scrollButtons="auto">
          {workbookData.map((sheet) => (
            <Tab key={sheet.id} label={sheet.name} icon={<TableChart fontSize="small" />} iconPosition="start" />
          ))}
        </StyledTabs>
      </Footer>
    </Container>
  );
};

export const XlsxPreview = memo(XlsxPreviewBase);
